'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@workspace/ui/lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex w-full min-w-0 flex-col gap-2', className)}
      {...props}
    />
  );
}

interface TabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  scrollable?: boolean;
}

function TabsList({ className, scrollable = true, ...props }: TabsListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const checkFades = useCallback(() => {
    if (scrollable && scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, [scrollable]);

  useEffect(() => {
    if (!scrollable) return;

    const node = scrollRef.current;
    if (node) {
      node.addEventListener('scroll', checkFades, { passive: true });
      checkFades();
    }
    window.addEventListener('resize', checkFades, { passive: true });

    return () => {
      if (node) node.removeEventListener('scroll', checkFades);
      window.removeEventListener('resize', checkFades);
    };
  }, [checkFades, scrollable]);

  const rightFade = useMemo(
    () =>
      scrollable &&
      showRightFade && (
        <div className="fade-in from-background dark:from-grey-1000 pointer-events-none absolute top-0 right-0 bottom-0 z-10 h-14 w-14 bg-gradient-to-l via-transparent to-transparent" />
      ),
    [scrollable, showRightFade]
  );

  const leftFade = useMemo(
    () =>
      scrollable &&
      showLeftFade && (
        <div className="fade-in from-background dark:from-grey-1000 pointer-events-none absolute top-0 bottom-0 left-0 z-10 h-14 w-14 bg-gradient-to-r via-transparent to-transparent" />
      ),
    [scrollable, showLeftFade]
  );

  if (!scrollable) {
    return (
      <div className="relative w-full">
        <TabsPrimitive.List
          data-slot="tabs-list"
          className={cn(
            'text-grey-600 dark:text-grey-400 dark:bg-grey-900 flex h-14 w-full items-center justify-center rounded-[20px] bg-neutral-100 p-1',
            className
          )}
          {...props}
        />
      </div>
    );
  }

  // Scrollable version (default behavior)
  return (
    <div className="relative w-full overflow-hidden">
      {leftFade}
      {rightFade}
      {/* Scrollable tab list */}
      <div className="scrollbar-hide w-full overflow-x-auto" ref={scrollRef}>
        <TabsPrimitive.List
          data-slot="tabs-list"
          className={cn(
            'text-grey-600 dark:text-grey-400 dark:bg-grey-900 flex h-14 w-full min-w-fit items-center justify-center rounded-[20px] bg-neutral-100 p-1',
            className
          )}
          {...props}
        />
      </div>
    </div>
  );
}

const TabsTrigger = React.memo(
  ({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) => {
    const baseStyles =
      'flex h-[calc(100%-1px)] cursor-pointer flex-1 items-center justify-center gap-1.5 rounded-2xl border border-transparent px-6 py-4 text-base whitespace-nowrap';
    const stateStyles =
      'data-[state=active]:font-medium data-[state=inactive]:font-normal disabled:pointer-events-none disabled:opacity-50';
    const textStyles =
      'text-grey-600 dark:text-grey-400 data-[state=active]:text-neutral-100 hover:text-neutral-200 dark:hover:text-neutral-100 dark:data-[state=active]:text-neutral-200';
    const backgroundStyles =
      'data-[state=active]:bg-neutral-200  dark:data-[state=active]:bg-grey-100';
    const miscStyles =
      "transition-[color,box-shadow] data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 md:w-[220px]";

    const combinedClassName = useMemo(
      () => cn(baseStyles, stateStyles, textStyles, backgroundStyles, miscStyles, className),
      [className]
    );

    return (
      <TabsPrimitive.Trigger data-slot="tabs-trigger" className={combinedClassName} {...props} />
    );
  }
);

TabsTrigger.displayName = 'TabsTrigger';

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none dark:text-neutral-100', className)}
      {...props}
    />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
